import { translateError } from "@/i18n/errors";
import { toast } from "sonner";
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useSpace } from "@/context/SpaceContext";
import { AppFormDialog } from "@/components/admin/AppFormDialog";
import { BulkUploader } from "@/components/admin/BulkUploader";
import { AdminAppRow } from "@/components/admin/AdminAppRow";
import { CategoryPick } from "@/components/admin/CategoryPick";
import { AtelierPick } from "@/components/admin/AtelierPick";
import { StripeInfo } from "@/components/market/StripeInfo";

const PAGE = 25;

// Searchable, paginated app list (fast with thousands of apps) + bulk APK uploader; shared by admin and developers.
export const AppsPanel = ({ prefix, maxApps, reloadKey = 0, onLoaded }) => {
  const { t } = useI18n();
  const { base } = useSpace();
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [all, setAll] = useState(0);
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => {
      const next = q.trim();
      setQuery((prev) => {
        if (prev !== next) setPage(0);
        return next;
      });
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const reload = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    let live = true;
    api.get(`${base}/apps`, { params: { q: query, skip: page * PAGE, limit: PAGE } }).then((res) => {
      if (!live) return;
      const total = Number(res.headers["x-total-count"] ?? res.data.length);
      setData({ items: res.data, total });
      if (!query) setAll(total);
      onLoaded?.({ items: res.data, total: query ? null : total });
    }).catch((err) => {
      if (!live) return;
      setData((d) => d || { items: [], total: 0 });
      toast.error(translateError(err, t));
    });
    return () => { live = false; };
  }, [t, base, query, page, tick, reloadKey, onLoaded]);

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;
  return (
    <section className="min-w-0 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-white md:text-base">
          {t("admin.apps")} <span className="text-[var(--gold)]" data-testid={`${prefix}-apps-count`}>({data ? (maxApps ? `${all}/${maxApps}` : all) : "…"})</span>
        </h2>
        <button type="button" className="btn-gold btn-sm" onClick={() => setOpen(true)} data-testid={`${prefix}-new-app-btn`}><Plus className="h-4 w-4" /> {t("admin.newApp")}</button>
      </div>
      <CategoryPick />
      <AtelierPick testId="bulk-atelier" />
      {base !== "/admin" && <StripeInfo dismissible testId="dev-upload-stripe-info" />}
      <BulkUploader onDone={reload} />
      <div className="relative">
        <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("apps.search")} className="h-11 bg-black/50 ps-9" data-testid={`${prefix}-apps-search`} />
      </div>
      {data === null && <Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" />}
      {data?.items.length === 0 && <p className="card-carbon rounded-2xl p-6 text-sm text-neutral-400" data-testid={`${prefix}-apps-empty`}>{t(query ? "apps.noMatch" : prefix === "dev" ? "dev.empty" : "admin.empty")}</p>}
      <ul className="space-y-3" data-testid={`${prefix}-apps-list`}>{data?.items.map((app) => <AdminAppRow key={app.id} app={app} onChanged={reload} />)}</ul>
      {pages > 1 && (
        <div className="flex items-center justify-between gap-3" data-testid={`${prefix}-apps-pagination`}>
          <button type="button" className="btn-gold btn-sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid={`${prefix}-apps-prev`}><ChevronLeft className="h-4 w-4 rtl:-scale-x-100" /> {t("apps.prev")}</button>
          <span className="text-xs text-neutral-400" data-testid={`${prefix}-apps-page`}>{t("apps.page", { p: page + 1, n: pages })}</span>
          <button type="button" className="btn-gold btn-sm" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} data-testid={`${prefix}-apps-next`}>{t("apps.next")} <ChevronRight className="h-4 w-4 rtl:-scale-x-100" /></button>
        </div>
      )}
      {open && <AppFormDialog open={open} onOpenChange={setOpen} onSaved={reload} />}
    </section>
  );
};
